import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { apiService } from '../services/api';
import { formatPrice, getProductImage } from '../utils/productHelpers';

const TestWrapper = styled.div`
  max-width: 800px;
  margin: 20px auto;
  padding: 20px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const TestTitle = styled.h2`
  color: #333;
  margin-bottom: 20px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e5e5e5;
  border-radius: 8px;
  font-size: 16px;
  margin-bottom: 20px;

  &:focus {
    outline: none;
    border-color: #ff6720;
  }
`;

const TestButton = styled.button`
  padding: 10px 20px;
  background: #ff6720;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  margin-right: 10px;
  margin-bottom: 10px;

  &:hover {
    background: #e55a1c;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const ResultsSection = styled.div`
  margin-top: 20px;
`;

const ResultItem = styled.div`
  display: flex;
  align-items: center;
  padding: 15px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  margin-bottom: 10px;
  background: #f9f9f9;
`;

const ProductImage = styled.img`
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 5px;
  margin-right: 15px;
`;

const ProductInfo = styled.div`
  flex: 1;

  h4 {
    margin: 0 0 5px 0;
    color: #333;
  }

  p {
    margin: 0;
    color: #666;
    font-size: 14px;
  }
`;

const PriceInfo = styled.div`
  font-weight: 600;
  color: #ff6720;
`;

const StatusMessage = styled.div`
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;

  &.loading {
    background: #e7f3ff;
    color: #0066cc;
  }

  &.error {
    background: #ffe7e7;
    color: #cc0000;
  }

  &.success {
    background: #e7f7e7;
    color: #006600;
  }
`;

const SearchTest = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [testResults, setTestResults] = useState([]);

  const testSearch = async (query) => {
    setIsLoading(true);
    setError(null);

    try {
      console.log(`Testing search for: "${query}"`);
      const response = await apiService.searchProducts(query, {
        status: 'active',
        limit: 10
      });

      console.log('Search response:', response);

      if (response.success) {
        setResults(response.data || []);
        setTestResults(prev => [...prev, {
          query,
          count: response.data?.length || 0,
          success: true,
          timestamp: new Date().toLocaleTimeString()
        }]);
      } else {
        setError('Search failed: ' + (response.error || 'Unknown error'));
        setResults([]);
      }
    } catch (err) {
      console.error('Search error:', err);
      setError('Search error: ' + err.message);
      setResults([]);
      setTestResults(prev => [...prev, {
        query,
        count: 0,
        success: false,
        error: err.message,
        timestamp: new Date().toLocaleTimeString()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const runPredefinedTests = async () => {
    const testQueries = ['chainsaw', 'mower', 'drill', 'matrix', 'cordless'];

    for (const query of testQueries) {
      await testSearch(query);
      // Add small delay between tests
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  };

  const testDirectAPI = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getProducts({
        status: 'active',
        limit: 5
      });

      console.log('Direct API response:', response);

      if (response.success) {
        setResults(response.data || []);
        setTestResults(prev => [...prev, {
          query: 'Direct API call',
          count: response.data?.length || 0,
          success: true,
          timestamp: new Date().toLocaleTimeString()
        }]);
      }
    } catch (err) {
      console.error('Direct API error:', err);
      setError('Direct API error: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <TestWrapper>
      <TestTitle>Search Functionality Test</TestTitle>

      <SearchInput
        type="text"
        placeholder="Enter search term..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onKeyPress={(e) => {
          if (e.key === 'Enter') {
            testSearch(searchQuery);
          }
        }}
      />

      <div>
        <TestButton
          onClick={() => testSearch(searchQuery)}
          disabled={isLoading || !searchQuery.trim()}
        >
          {isLoading ? 'Searching...' : 'Test Search'}
        </TestButton>

        <TestButton
          onClick={runPredefinedTests}
          disabled={isLoading}
        >
          Run Test Suite
        </TestButton>

        <TestButton
          onClick={testDirectAPI}
          disabled={isLoading}
        >
          Test Direct API
        </TestButton>
      </div>

      {error && (
        <StatusMessage className="error">
          <strong>Error:</strong> {error}
        </StatusMessage>
      )}

      {isLoading && (
        <StatusMessage className="loading">
          <strong>Loading:</strong> Searching for products...
        </StatusMessage>
      )}

      {testResults.length > 0 && (
        <ResultsSection>
          <h3>Test Results:</h3>
          {testResults.map((test, index) => (
            <StatusMessage
              key={index}
              className={test.success ? 'success' : 'error'}
            >
              <strong>{test.timestamp}:</strong> "{test.query}" →
              {test.success ? ` Found ${test.count} products` : ` Failed: ${test.error}`}
            </StatusMessage>
          ))}
        </ResultsSection>
      )}

      {results.length > 0 && (
        <ResultsSection>
          <h3>Search Results ({results.length} found):</h3>
          {results.map((product) => (
            <ResultItem key={product.id}>
              <ProductImage
                src={getProductImage(product)}
                alt={product.name}
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/60x60?text=No+Image';
                }}
              />
              <ProductInfo>
                <h4>{product.name}</h4>
                <p>SKU: {product.sku || 'N/A'}</p>
                <p>Brand: {product.brand || 'N/A'}</p>
                <p>Category: {product.category_name || 'N/A'}</p>
              </ProductInfo>
              <PriceInfo>
                {product.sale_price
                  ? formatPrice(product.sale_price)
                  : formatPrice(product.price)
                }
              </PriceInfo>
            </ResultItem>
          ))}
        </ResultsSection>
      )}
    </TestWrapper>
  );
};

export default SearchTest;
